<script setup lang="ts">
    import { useUserStore } from "@/stores/user";

    interface Props {
        size?: string | number;
        rounded?: string;
        avatarPath?: string | null;
        firstName?: string;
        lastName?: string;
    }

    const props = withDefaults(defineProps<Props>(), {
        size: undefined,
        rounded: undefined,
        avatarPath: undefined,
        firstName: undefined,
        lastName: undefined,
    });

    // Uses
    const userStore = useUserStore();

    // Computed

    /**
     * Avatar image to render, an explicitly passed path always wins over the logged in user.
     */
    const resolvedAvatarPath = computed<string | null>(() => {
        const path = props.avatarPath !== undefined ? props.avatarPath : userStore.user?.avatar_path;

        return path ?? null;
    });

    /**
     * Initials shown whenever no avatar image is available.
     */
    const initials = computed<string>(() => {
        const firstName = props.firstName ?? userStore.user?.first_name;
        const lastName = props.lastName ?? userStore.user?.last_name;

        return ((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase() || '?';
    });

    /**
     * Scale the initials with the avatar so they stay readable at every size.
     */
    const initialsFontSize = computed<string>(() => {
        const size = Number(props.size);

        return Number.isFinite(size) && size > 0 ? `${Math.round(size * 0.4)}px` : '0.875rem';
    });
</script>

<template>
    <VAvatar
        :rounded="rounded"
        :size="size"
        color="#009ee3"
        variant="flat"
    >
        <VImg v-if="resolvedAvatarPath" :src="resolvedAvatarPath" cover/>
        <span v-else :style="{ fontSize: initialsFontSize }" class="avatar-initials">{{ initials }}</span>
    </VAvatar>
</template>

<style scoped lang="scss">
    .avatar-initials {
        color: #FFFFFF;
        font-weight: 500;
        letter-spacing: 0.05em;
        user-select: none;
    }
</style>
